import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import Layout from "layouts/LayoutMasterDetail";
import ServiceMetricCell from "features/services/shared/ServiceMetricCell";
import { getServiceRouteTimeColumns } from "features/services/shared/serviceRouteTimeColumns";
import { StoreContext } from "shared/context/store-context";
import { SERVICE_TYPES } from "@routebot/constants";
import { noPersonelSchoolBlocker } from "shared/utility/blocker-conditions";
import { getCurrentLanguage } from "shared/utility/lang";
import {
  isActiveOptions_tr,
  isActiveOptions_en,
  personelServiceRouteTypes_tr,
  personelServiceRouteTypes_en,
} from "shared/util/types";

import TourBuilderItem from "./TourBuilderItem";

// Tour Builder — list + master/detail host.
//
// Tours are stored as PersonelService(serviceType=TOUR). They live in the store's
// `standartPersonelServices` slice (isshift=false) but are excluded from the Standard /
// Transfer personel tabs (which filter by their own serviceType), so this tab is the
// single place they surface. Reuses the store's Insert/Update/Delete personel-service ops.
const TourBuilderList = ({ isNewItem }) => {
  const { t } = useTranslation();

  const {
    standartPersonelServices,
    personelSchoolOptions,
    driverOptions,
    schools,
    InsertPersonelService,
    DeletePersonelService,
    UpdatePersonelService,
  } = useContext(StoreContext);

  const pageItems = standartPersonelServices.filter((item) => item.serviceType === SERVICE_TYPES.TOUR);

  const isActiveOptions = getCurrentLanguage() === "tr" ? isActiveOptions_tr : isActiveOptions_en;
  const personelServiceRouteTypes =
    getCurrentLanguage() === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;

  const listColumns = [
    { field: "date", headerName: t("date"), width: 130 },
    { field: "name", headerName: t("tourBuilder.name"), width: 280 },
    ...getServiceRouteTimeColumns(t),
    {
      field: "routeType",
      headerName: t("serviceType"),
      width: 110,
      valueGetter: (params) =>
        personelServiceRouteTypes.find((item) => item?.value?.toString() === params.row?.routeType?.toString())
          ?.label || "",
    },
    {
      field: "school",
      headerName: t("tourBuilder.anchorSchool"),
      width: 200,
      valueGetter: (params) =>
        personelSchoolOptions.find((o) => o?.value?.toString() === params?.row?.school?.id?.toString())?.label || "",
    },
    {
      field: "drivers",
      headerName: t("tourBuilder.driver"),
      width: 200,
      valueGetter: (params) => {
        try {
          return (
            driverOptions.find((o) => o.value.toString() === params?.row?.drivers?.[0]?.id?.toString())?.label || ""
          );
        } catch (e) {
          return "";
        }
      },
    },
    {
      field: "plate",
      headerName: t("plate"),
      width: 120,
      valueGetter: (params) => (params.row?.plate ? String(params.row.plate).trim() : ""),
    },
    {
      field: "agency",
      headerName: t("tourBuilder.agency"),
      width: 140,
      valueGetter: (params) => (params.row?.agency ? String(params.row.agency).trim() : ""),
    },
    {
      field: "personCount",
      headerName: t("tourBuilder.personCount"),
      width: 120,
      valueGetter: (params) => (typeof params.row?.personCount === "number" ? String(params.row.personCount) : ""),
    },
    {
      field: "numOfStops",
      headerName: t("tourBuilder.stopCount"),
      width: 120,
      valueGetter: (params) => params.row?.users?.length?.toString() || "-",
    },
    {
      field: "isActive",
      headerName: t("status"),
      width: 110,
      valueGetter: (params) => (params.row?.isActive === false ? isActiveOptions[0].label : isActiveOptions[1].label),
    },
    {
      field: "routeMetrics",
      headerName: t("routeMetrics"),
      width: 180,
      valueGetter: (params) => {
        const duration = Number(params.row?.totalDuration);
        return Number.isFinite(duration) ? duration : 0;
      },
      renderCell: (params) => <ServiceMetricCell row={params.row} />,
    },
  ];

  return (
    <Layout
      itemType="tour"
      items={pageItems}
      listName="main_tours"
      listColumns={listColumns}
      SelectedItemComponent={TourBuilderItem}
      NewItemComponent={TourBuilderItem}
      DeleteItem={DeletePersonelService}
      InsertItem={InsertPersonelService}
      UpdateItem={UpdatePersonelService}
      isAlwaysFullScreen={true}
      hideCreateNewItemWhenSelected={true}
      isNewItem={isNewItem}
      blockerConditions={[noPersonelSchoolBlocker(schools)]}
    />
  );
};

export default TourBuilderList;
